<template>
  <div class="sale-banner">
    <div
      class="relative flex flex-wrap items-center justify-center px-2 md:py-4 py-4 overflow-hidden font-bold text-center border-b-2 gap-y-1.5"
      :style="{ borderBottom: '2px solid ' + bannerBorderColor, color: textColor, backgroundColor: backgroundColor }">
      <span class="z-20 flex items-center leading-none rounded gap-x-2">
        <span class="-mt-1 text-2xl" v-html="startIcon"></span>
        <span class="font-bold shadow-inner font-ww-medium" style="font-size: clamp(14px, 3.3vw, 22px)"
          v-html="bannerText"></span>
        <span class="-mt-1 text-2xl" v-html="endIcon"></span>
      </span>

      <div v-for="(position, index) in positions" :key="index"
        class="absolute floating-percentage opacity-20 lg:opacity-40 2xl:opacity-100 md:opacity-20"
        :style="position.position">
        <div v-if="svgType === 'richText' && !svgImgSrc" v-html="svgIcon"></div>
        <img v-else :src="svgImgSrc" width="28" height="28" alt="Floating SVG Icon" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.floating-percentage {
  animation: float 5s infinite;
}

@keyframes float {
  0% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-20px);
  }

  100% {
    transform: translateY(0);
  }
}
</style>

<script setup>

const props = defineProps({
  bannerText: {
    type: String,
    default: "BLACK FRIDAY SALE! 83% OFF + FREE GIFTS"
  },
  bannerBorderColor: {
    type: String,
    default: "#E48414"
  },
  textColor: {
    type: String,
    default: "#FFFFFF"
  },
  backgroundColor: {
    type: String,
    default: "#AC4F35"
  },
  iconColor: {
    type: String,
    default: "#E48414"
  },
  svgIcon: {
    type: String,
    default: `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40px" height="40px">
        <path fill="#E48414" d="M16 17H5V7h11l3.55 5m-1.92-6.16C17.27 5.33 16.67 5 16 5H5c-1.1 0-2 .9-2 2v10a2 2 0 0 0 2 2h11c.67 0 1.27-.34 1.63-.85L22 12zM13.8 8L15 9.2L8.2 16L7 14.8m1.45-6.77c.78 0 1.42.64 1.42 1.42s-.64 1.42-1.42 1.42s-1.42-.64-1.42-1.42s.64-1.42 1.42-1.42m5.1 5.1c.78 0 1.42.64 1.42 1.42s-.64 1.42-1.42 1.42s-1.42-.64-1.42-1.42s.64-1.42 1.42-1.42"/>
      </svg>
    `
  },
  svgType: {
    type: String,
    default: "richText", // options: "richText" or "img"
  },
  svgImgSrc: {
    type: String,
    default: ""
  },
  startIcon: {
    type: String,
    default: "🎉"
  },
  endIcon: {
    type: String,
    default: "🎁"
  },
  positions: {
    type: Array,
    default: () => [
      { position: "top:35px; left:30px" },
      { position: "top:35px; right:30px" },
      { position: "bottom:20px; left:50px" },
      { position: "bottom:20px; right:50px" },
      { position: "top:50%; left:25%" },
      { position: "top:50%; right:25%" },
      { position: "bottom:50%; left:30%" },
      { position: "bottom:50%; right:30%" }
    ]
  }
});

// Compute positions as style objects
const positionList = computed(() => {
  props.positions.map(position => {
    position.position.split(';').reduce((styleObj, stylePair) => {
      const [key, value] = stylePair.split(':');
      styleObj[key.trim()] = value.trim();
      return styleObj;
    }, {})
  })
}
);

defineBuilderComponent({
  friendlyName: "Sale Banner",
  inputs: [
    {
      name: "bannerText",
      type: "richText",
      friendlyName: "Banner Text",
      defaultValue: "BLACK FRIDAY SALE! 83% OFF + FREE GIFTS"
    },
    {
      name: "bannerBorderColor",
      type: "color",
      friendlyName: "Banner Border Color",
      defaultValue: "#E48414"
    },
    {
      name: "textColor",
      type: "color",
      friendlyName: "Text Color",
      defaultValue: "#FFFFFF"
    },
    {
      name: "backgroundColor",
      type: "color",
      friendlyName: "Background Color",
      defaultValue: "#AC4F35"
    },
    {
      name: "iconColor",
      type: "color",
      friendlyName: "Icon Color",
      defaultValue: "#E48414"
    },
    {
      name: "svgIcon",
      type: "richText",
      friendlyName: "SVG Icon",
      defaultValue: `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40px" height="40px">
          <path fill="#E48414" d="M16 17H5V7h11l3.55 5m-1.92-6.16C17.27 5.33 16.67 5 16 5H5c-1.1 0-2 .9-2 2v10a2 2 0 0 0 2 2h11c.67 0 1.27-.34 1.63-.85L22 12zM13.8 8L15 9.2L8.2 16L7 14.8m1.45-6.77c.78 0 1.42.64 1.42 1.42s-.64 1.42-1.42 1.42s-1.42-.64-1.42-1.42s.64-1.42 1.42-1.42m5.1 5.1c.78 0 1.42.64 1.42 1.42s-.64 1.42-1.42 1.42s-1.42-.64-1.42-1.42s.64-1.42 1.42-1.42"/>
        </svg>
      `
    },
    {
      name: "svgType",
      type: "string",
      friendlyName: "SVG Type",
      enum: ["richText", "img"],
      defaultValue: "richText"
    },
    {
      name: "svgImgSrc",
      type: "file",
      friendlyName: "SVG Image Source",
      allowedFileTypes: ["svg", "png", "jpg", "jpeg"],
      defaultValue: ""
    },
    {
      name: "startIcon",
      type: "string",
      friendlyName: "Start Icon",
      defaultValue: "🎉"
    },
    {
      name: "endIcon",
      type: "string",
      friendlyName: "End Icon",
      defaultValue: "🎁"
    },
    {
      name: "positions",
      type: "list",
      friendlyName: "Floating SVG Positions",
      subFields: [
        {
          name: "position",
          type: "string",
          friendlyName: "Position",
          defaultValue: ""
        }
      ],
      defaultValue: [
        { position: "top:35px; left:30px" },
        { position: "top:35px; right:30px" },
        { position: "bottom:20px; left:50px" },
        { position: "bottom:20px; right:50px" },
        { position: "top:50%; left:25%" },
        { position: "top:50%; right:25%" },
        { position: "bottom:50%; left:30%" },
        { position: "bottom:50%; right:30%" }
      ]
    }
  ]
});
</script>
